import { useCallback, useEffect, useRef, useState } from 'react';
import SiteLayout from './SiteLayout';
import Markdown from './Markdown';
import { links, HOME_PHOTOS, todayInLondon } from './siteLinks';
import ResultsDocs from './ResultsDocs';
import EventVideos from './EventVideos';
import PageDocuments from './PageDocuments';
import PageImages, { PhotoPicker } from './PageImages';
import useSiteImages from './useSiteImages';
import HILLS from '../../content/hillsIndex';

// Web Admin (/site-admin): the public website's admin portal. Login is a
// magic link, as for the other portals (backend/site_admin_routes.py).
// Sections: the notice bar, the website's editable pages (each page's editor
// also has the images, PageImages.jsx, and the documents, PDFs like the rule
// books, PageDocuments.jsx), who each contact-form topic is sent to, and how
// archived results documents are filed (ResultsDocs.jsx), and videos of
// events in the Results Hub (EventVideos.jsx). Photo albums runs the nightly
// photo sync on demand.

const SESSION_KEY = 'nhcaSiteAdminSession';

function readSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)) || null; } catch { return null; }
}
function writeSession(s) {
  try { s ? localStorage.setItem(SESSION_KEY, JSON.stringify(s)) : localStorage.removeItem(SESSION_KEY); } catch { /* private mode */ }
}

function Login() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      const res = await fetch('/api/site-admin/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      setMessage(res.ok ? 'If that address is registered for Web Admin, a login link is on its way. Check your inbox.' : (data.error || 'Something went wrong.'));
      setState(res.ok ? 'sent' : 'idle');
    } catch {
      setMessage('Could not reach the server. Please try again.');
      setState('idle');
    }
  };

  return (
    <div className="site-admin-login">
      <h2>Log in</h2>
      <p>Enter your email address and we&apos;ll send you a login link.</p>
      {state === 'sent' ? <p className="site-admin-ok" role="status">{message}</p> : (
        <form onSubmit={submit} className="site-admin-login-form">
          <label className="site-field">
            <span>Email address</span>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
          </label>
          {message && <p className="site-field-error" role="alert">{message}</p>}
          <button type="submit" className="site-btn site-btn--orange" disabled={state === 'sending'}>
            {state === 'sending' ? 'Sending…' : 'Send login link'}
          </button>
        </form>
      )}
    </div>
  );
}

// A collapsible admin section. Closed by default; each section remembers
// whether it was left open, per browser.
function AdminSection({ id, title, summary, children }) {
  const storageKey = `nhcaSiteAdminOpen:${id}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const toggle = (e) => {
    const isOpen = e.currentTarget.open;
    setOpen(isOpen);
    try { localStorage.setItem(storageKey, isOpen ? '1' : '0'); } catch { /* private mode */ }
  };
  return (
    <details className="site-admin-section" open={open} onToggle={toggle}>
      <summary>
        <span className="site-admin-section-title">{title}</span>
        {summary && <span className="site-admin-section-summary">{summary}</span>}
      </summary>
      <div className="site-admin-section-body">{children}</div>
    </details>
  );
}

function ContactRow({ contact, session, onSaved, onExpired }) {
  const [draft, setDraft] = useState({ label: contact.label, description: contact.description, recipient_email: contact.recipient_email || '' });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const dirty = draft.label !== contact.label || draft.description !== contact.description
    || draft.recipient_email !== (contact.recipient_email || '');

  const save = async () => {
    setState('saving');
    setError('');
    const res = await fetch(`/api/site-admin/contacts/${contact.key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.session_token}` },
      body: JSON.stringify(draft),
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) {
      onSaved(data);
      setState('saved');
    } else {
      setError(data.error || 'Could not save. Please try again.');
      setState('idle');
    }
  };

  const set = (key) => (e) => { setDraft(d => ({ ...d, [key]: e.target.value })); setState('idle'); };

  return (
    <div className="site-admin-contact">
      <div className="site-admin-contact-fields">
        <label className="site-field">
          <span>Topic</span>
          <input type="text" value={draft.label} onChange={set('label')} maxLength={80} />
        </label>
        <label className="site-field">
          <span>Description</span>
          <input type="text" value={draft.description} onChange={set('description')} maxLength={200} />
        </label>
        <label className="site-field">
          <span>Send to</span>
          <input type="email" value={draft.recipient_email} onChange={set('recipient_email')} placeholder="Leave empty to hide this topic" />
        </label>
      </div>
      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">
          {!contact.recipient_email && <strong className="site-admin-hidden">Hidden from the form. </strong>}
          {contact.updated_by && contact.updated_at
            ? `Last changed by ${contact.updated_by}, ${new Date(contact.updated_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`
            : ''}
        </span>
        {error && <span className="site-field-error" role="alert">{error}</span>}
        {state === 'saved' && !dirty && <span className="site-admin-ok" role="status">Saved</span>}
        <button type="button" className="site-btn site-btn--ink" onClick={save} disabled={!dirty || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Contacts({ session, onExpired }) {
  const [contacts, setContacts] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetch('/api/site-admin/contacts', { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load');
        setContacts(data.contacts);
      })
      .catch(e => setError(e.message));
  }, [session, onExpired]);

  useEffect(() => { load(); }, [load]);

  const hidden = contacts ? contacts.filter(c => !c.recipient_email).length : 0;
  const summary = contacts
    ? `${contacts.length} topics${hidden ? `, ${hidden} hidden` : ''}`
    : '';

  return (
    <AdminSection id="contacts" title="Contact form" summary={summary}>
      <p>Where messages from the <a href="/contact">contact page</a> go, for each topic. Addresses are never shown on the website, and replying to a message emails the visitor directly.</p>
      {error && <p className="site-field-error">{error}</p>}
      {!contacts && !error && <p>Loading…</p>}
      {contacts && contacts.map(c => (
        <ContactRow key={c.key} contact={c} session={session} onExpired={onExpired}
          onSaved={updated => setContacts(list => list.map(x => (x.key === updated.key ? updated : x)))} />
      ))}
    </AdminSection>
  );
}

function when(iso) {
  return iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

function PageEditor({ slug, session, onClose, onSaved, onExpired }) {
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState({ title: '', body_md: '' });
  const [loaded, setLoaded] = useState(null);       // revision shown in the editor, if not the live page
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');
  const textRef = useRef(null);
  const images = useSiteImages(session, onExpired);
  const auth = { Authorization: `Bearer ${session.session_token}` };

  const load = useCallback(() => {
    fetch(`/api/site-admin/pages/${slug}`, { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load the page.');
        setPage(data);
        setDraft({ title: data.title, body_md: data.body_md });
        setLoaded(null);
      })
      .catch(e => setMessage(e.message || 'Could not load the page.'));
  }, [slug, session, onExpired]);

  useEffect(() => { load(); }, [load]);

  const dirty = page && (draft.title !== page.title || draft.body_md !== page.body_md);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setState('saving');
    setMessage('');
    const res = await fetch(`/api/site-admin/pages/${slug}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...auth }, body: JSON.stringify(draft),
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) {
      setState('idle');
      setMessage(data.unchanged ? 'Saved: nothing had changed.' : 'Saved. The page on the website now shows this version.');
      onSaved();
      load();
    } else {
      setState('idle');
      setMessage(data.error || 'Could not save. Please try again.');
    }
  };

  const loadRevision = async (rev) => {
    const res = await fetch(`/api/site-admin/pages/${slug}/revisions/${rev.id}`, { headers: auth }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res && res.ok ? await res.json() : null;
    if (!data) { setMessage('Could not load that version.'); return; }
    setDraft({ title: data.title, body_md: data.body_md });
    setLoaded(data);
    setMessage('');
  };

  // Markdown at the cursor, made by `make` from the selected text (if any).
  const insertAt = (make) => {
    const el = textRef.current;
    const text = draft.body_md;
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const link = make(text.slice(start, end).trim());
    setDraft(d => ({ ...d, body_md: text.slice(0, start) + link + text.slice(end) }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + link.length, start + link.length);
    });
  };

  const close = () => {
    if (dirty && !window.confirm('Leave without saving your changes?')) return;
    onClose();
  };

  if (!page) return <p>{message || 'Loading…'}</p>;

  return (
    <div className="site-admin-editor">
      <div className="site-admin-editor-bar">
        <button type="button" className="site-btn site-btn--ghost-dark" onClick={close}>← All pages</button>
        <a href={`/${slug}`} target="_blank" rel="noreferrer">View the page on the website</a>
      </div>
      {loaded && (
        <p className="site-admin-notice">
          Showing the version saved {when(loaded.saved_at)} by {loaded.saved_by}.
          {dirty ? ' Save it to make it the live page again, or' : ' It is the same as the live page, so there is nothing to save:'}
          {' '}<button type="button" className="site-link-button" onClick={load}>go back to the live version</button>.
        </p>
      )}
      <label className="site-field">
        <span>Title</span>
        <input type="text" value={draft.title} maxLength={120} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
      </label>
      <div className="site-admin-editor-panes">
        <label className="site-field">
          <span>Text</span>
          <textarea ref={textRef} className="site-admin-md" value={draft.body_md} spellCheck
            onChange={e => setDraft(d => ({ ...d, body_md: e.target.value }))} />
          <span className="site-admin-help">
            <code>## Heading</code> · <code>**bold**</code> · <code>- list item</code> · <code>[link text](/calendar)</code> · <code>![description](image address)</code> · <code>![title](YouTube link)</code> for a video · a blank line starts a new paragraph · pictures and PDFs: Images and Documents, below
          </span>
          {slug.startsWith('hills-') && (
            <span className="site-admin-help">
              The website adds a <strong>Photos</strong> list under this text by itself: every album of this hill in the photo archive, newest first. New events appear in it overnight, so there&apos;s no need to add photo links here.
            </span>
          )}
        </label>
        <div className="site-field">
          <span>Preview</span>
          <div className="site-admin-preview"><h1 className="site-admin-preview-title">{draft.title}</h1><Markdown>{draft.body_md}</Markdown></div>
        </div>
      </div>
      <PageImages {...images} session={session} onExpired={onExpired}
        onInsert={img => insertAt(selected => `![${selected || img.title}](${img.url})`)}
        open={page.body_md.includes('/images/')} />
      <PageDocuments session={session} onExpired={onExpired}
        onInsert={doc => insertAt(selected => `[${selected || `${doc.title} (PDF)`}](${doc.url})`)}
        open={page.body_md.includes('/documents/')} />
      <div className="site-admin-contact-foot">
        <span className="site-admin-meta">
          {page.saved ? `Live version saved by ${page.updated_by}, ${when(page.updated_at)}` : 'The website shows the starting text from the old site. Nobody has saved this page yet.'}
        </span>
        {message && <span className={message.startsWith('Saved') ? 'site-admin-ok' : 'site-field-error'} role="status">{message}</span>}
        <button type="button" className="site-btn site-btn--orange" onClick={save} disabled={!dirty || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save'}
        </button>
      </div>
      {page.revisions.length > 0 && (
        <details className="site-admin-revisions">
          <summary>Earlier versions ({page.revisions.length})</summary>
          <ul>
            {page.revisions.map(r => (
              <li key={r.id}>
                <span>{when(r.saved_at)}, {r.saved_by}</span>
                <button type="button" className="site-link-button" onClick={() => loadRevision(r)}>Load into the editor</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ── Home page: a form over the 'home' page's JSON fields ──────────────────

function setIn(doc, path, value) {
  const next = structuredClone(doc);
  let node = next;
  path.slice(0, -1).forEach(k => {
    if (node[k] === undefined || node[k] === null) node[k] = typeof k === 'number' ? [] : {};
    node = node[k];
  });
  node[path[path.length - 1]] = value;
  return next;
}

function getIn(doc, path) {
  return path.reduce((node, k) => (node == null ? undefined : node[k]), doc);
}

function HomeField({ doc, path, label, onChange, multiline, placeholder }) {
  const value = getIn(doc, path) ?? '';
  const props = { value, placeholder, maxLength: 600, onChange: e => onChange(setIn(doc, path, e.target.value)) };
  return (
    <label className="site-field">
      <span>{label}</span>
      {multiline ? <textarea rows={3} {...props} /> : <input type="text" {...props} />}
    </label>
  );
}

function HomeLink({ doc, path, label, onChange }) {
  return (
    <div className="site-admin-pair">
      <HomeField doc={doc} path={[...path, 'label']} label={`${label}: text`} onChange={onChange} />
      <HomeField doc={doc} path={[...path, 'href']} label={`${label}: goes to`} onChange={onChange} placeholder="/give-it-a-go or https://…" />
    </div>
  );
}

// A form over a FIELD_PAGES page's JSON (backend site_pages.py): load, edit
// with the fields given by `renderFields`, save, and earlier versions.
function FieldPageEditor({ slug, title, note, viewHref, renderFields, summarize, session, onExpired }) {
  const [page, setPage] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState('');
  const auth = { Authorization: `Bearer ${session.session_token}` };

  const load = useCallback(() => {
    fetch(`/api/site-admin/pages/${slug}`, { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load the page.');
        setPage(data);
        setDoc(JSON.parse(data.body_md));
        setLoaded(null);
      })
      .catch(e => setMessage(e.message || 'Could not load the page.'));
  }, [slug, session, onExpired]);

  useEffect(() => { load(); }, [load]);

  const dirty = page && doc && JSON.stringify(doc) !== JSON.stringify(JSON.parse(page.body_md));

  const save = async () => {
    setState('saving');
    setMessage('');
    const res = await fetch(`/api/site-admin/pages/${slug}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ title, body_md: JSON.stringify(doc) }),
    }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    setState('idle');
    if (res && res.ok) {
      setMessage(data.unchanged ? 'Saved: nothing had changed.' : 'Saved. The website now shows this version.');
      load();
    } else {
      setMessage(data.error || 'Could not save. Please try again.');
    }
  };

  const loadRevision = async (rev) => {
    const res = await fetch(`/api/site-admin/pages/${slug}/revisions/${rev.id}`, { headers: auth }).catch(() => null);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res && res.ok ? await res.json() : null;
    if (!data) { setMessage('Could not load that version.'); return; }
    setDoc(JSON.parse(data.body_md));
    setLoaded(data);
    setMessage('');
  };

  const summary = page && summarize ? summarize(JSON.parse(page.body_md))
    : page ? (page.saved ? `last changed by ${page.updated_by}` : 'starting content') : '';

  return (
    <AdminSection id={slug} title={title} summary={summary}>
      {!doc ? <p>{message || 'Loading…'}</p> : (
        <div className="site-admin-editor">
          <div className="site-admin-editor-bar">
            <span className="site-admin-meta">{note}</span>
            <a href={viewHref} target="_blank" rel="noreferrer">View the page</a>
          </div>
          {loaded && (
            <p className="site-admin-notice">
              Showing the version saved {when(loaded.saved_at)} by {loaded.saved_by}.
              {dirty ? ' Save it to make it live again, or' : ' It is the same as the live version, so there is nothing to save:'}
              {' '}<button type="button" className="site-link-button" onClick={load}>go back to the live version</button>.
            </p>
          )}
          {renderFields(doc, setDoc)}
          <div className="site-admin-contact-foot">
            <span className="site-admin-meta">
              {page.saved ? `Live version saved by ${page.updated_by}, ${when(page.updated_at)}` : 'Nobody has saved this yet.'}
            </span>
            {message && <span className={message.startsWith('Saved') ? 'site-admin-ok' : 'site-field-error'} role="status">{message}</span>}
            <button type="button" className="site-btn site-btn--orange" onClick={save} disabled={!dirty || state === 'saving'}>
              {state === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
          {page.revisions.length > 0 && (
            <details className="site-admin-revisions">
              <summary>Earlier versions ({page.revisions.length})</summary>
              <ul>
                {page.revisions.map(r => (
                  <li key={r.id}>
                    <span>{when(r.saved_at)}, {r.saved_by}</span>
                    <button type="button" className="site-link-button" onClick={() => loadRevision(r)}>Load into the editor</button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </AdminSection>
  );
}

// A home page photo: the chosen image's address and description are saved
// in the page (<section>.image, .imageAlt); blank = the original photo.
function HomePhoto({ doc, section, label, original, images, onChange }) {
  return (
    <PhotoPicker label={label} images={images} value={doc[section]?.image || ''} original={original}
      onPick={img => onChange(setIn(setIn(doc, [section, 'image'], img ? img.url : ''), [section, 'imageAlt'], img ? img.title : ''))} />
  );
}

function HomeEditor(props) {
  const images = useSiteImages(props.session, props.onExpired);
  return (
    <FieldPageEditor {...props} slug="home" title="Home page" viewHref={links.home}
      note="The quick links, photos, next event and classes on the home page update themselves."
      renderFields={(doc, setDoc) => (
        <>
          <fieldset className="site-admin-group">
            <legend>Top of the page</legend>
            <HomeField doc={doc} path={['hero', 'kicker']} label="Small heading" onChange={setDoc} />
            <HomeField doc={doc} path={['hero', 'title']} label="Big heading" onChange={setDoc} />
            <HomeField doc={doc} path={['hero', 'text']} label="Text" onChange={setDoc} multiline />
            <HomeLink doc={doc} path={['hero', 'primary']} label="Orange button" onChange={setDoc} />
            <HomeLink doc={doc} path={['hero', 'secondary']} label="Second button" onChange={setDoc} />
            <HomePhoto doc={doc} section="hero" label="Big photo behind the heading" original={HOME_PHOTOS.hero}
              images={images.images} onChange={setDoc} />
          </fieldset>
          <fieldset className="site-admin-group">
            <legend>Featured video</legend>
            <label className="site-admin-check">
              <input type="checkbox" checked={!!doc.video?.show} onChange={e => setDoc(setIn(doc, ['video', 'show'], e.target.checked))} />
              Show a video on the home page
            </label>
            <HomeField doc={doc} path={['video', 'url']} label="YouTube link" onChange={setDoc} placeholder="https://www.youtube.com/watch?v=…" />
            <HomeField doc={doc} path={['video', 'kicker']} label="Small heading" onChange={setDoc} />
            <HomeField doc={doc} path={['video', 'title']} label="Heading" onChange={setDoc} />
            <HomeField doc={doc} path={['video', 'text']} label="Text" onChange={setDoc} multiline />
          </fieldset>
          <fieldset className="site-admin-group">
            <legend>&ldquo;Anyone can have a go&rdquo; panel</legend>
            <HomeField doc={doc} path={['intro', 'kicker']} label="Small heading" onChange={setDoc} />
            <HomeField doc={doc} path={['intro', 'title']} label="Heading" onChange={setDoc} />
            <HomeField doc={doc} path={['intro', 'text']} label="Text" onChange={setDoc} multiline />
            {[0, 1, 2].map(i => (
              <div key={i} className="site-admin-pair">
                <HomeField doc={doc} path={['intro', 'facts', i, 'value']} label={`Fact ${i + 1}: figure`} onChange={setDoc} placeholder="£40–70" />
                <HomeField doc={doc} path={['intro', 'facts', i, 'label']} label={`Fact ${i + 1}: what it is`} onChange={setDoc} placeholder="typical entry" />
              </div>
            ))}
            <HomeField doc={doc} path={['intro', 'small']} label="Small print" onChange={setDoc} multiline />
            <HomeLink doc={doc} path={['intro', 'button']} label="Button" onChange={setDoc} />
            <HomePhoto doc={doc} section="intro" label="Photo beside the panel" original={HOME_PHOTOS.intro}
              images={images.images} onChange={setDoc} />
          </fieldset>
          <PageImages {...images} session={props.session} onExpired={props.onExpired} />
        </>
      )}
    />
  );
}

// The picture on each venue's card on the hills page: {slug: {image, imageAlt}};
// blank = the photo from the old website (hillsIndex.js).
function HillPicturesEditor(props) {
  const images = useSiteImages(props.session, props.onExpired);
  return (
    <FieldPageEditor {...props} slug="hill-pictures" title="Hill pictures" viewHref="/hills"
      note="The picture on each venue's card on the hills page. The cards are wide and short, so a picture is cut to fit: a landscape photo works best."
      summarize={doc => {
        const n = Object.values(doc).filter(v => v?.image).length;
        return n ? `${n} changed` : 'the original photos';
      }}
      renderFields={(doc, setDoc) => (
        <>
          <fieldset className="site-admin-group">
            <legend>Venues</legend>
            {HILLS.map(h => (
              <PhotoPicker key={h.slug} label={h.name} images={images.images} value={doc[h.slug]?.image || ''} original={h.image}
                onPick={img => setDoc({ ...doc, [h.slug]: { image: img ? img.url : '', imageAlt: img ? img.title : '' } })} />
            ))}
          </fieldset>
          <PageImages {...images} session={props.session} onExpired={props.onExpired} />
        </>
      )}
    />
  );
}

function PhotosPageEditor(props) {
  return (
    <FieldPageEditor {...props} slug="photos" title="Photos page" viewHref="/photos"
      note="The albums on the photos page update themselves every night from the photo archive (or now: Photo albums, below)."
      renderFields={(doc, setDoc) => (
        <>
          <fieldset className="site-admin-group">
            <legend>Introduction</legend>
            <HomeField doc={doc} path={['intro']} label="Text under the heading" onChange={setDoc} multiline />
          </fieldset>
          <fieldset className="site-admin-group">
            <legend>Links (e.g. Instagram)</legend>
            {[0, 1, 2, 3].map(i => (
              <HomeLink key={i} doc={doc} path={['links', i]} label={`Link ${i + 1}`} onChange={setDoc} />
            ))}
            <p className="site-admin-help">Leave a link&apos;s text empty to hide it.</p>
          </fieldset>
        </>
      )}
    />
  );
}

function VideosPageEditor(props) {
  return (
    <FieldPageEditor {...props} slug="videos" title="Videos page" viewHref="/videos"
      note="The videos themselves are added in Event videos, below."
      renderFields={(doc, setDoc) => (
        <>
          <fieldset className="site-admin-group">
            <legend>Introduction</legend>
            <HomeField doc={doc} path={['intro']} label="Text under the heading" onChange={setDoc} multiline />
          </fieldset>
          <fieldset className="site-admin-group">
            <legend>Links (e.g. YouTube)</legend>
            {[0, 1, 2, 3].map(i => (
              <HomeLink key={i} doc={doc} path={['links', i]} label={`Link ${i + 1}`} onChange={setDoc} />
            ))}
            <p className="site-admin-help">Leave a link&apos;s text empty to hide it.</p>
          </fieldset>
        </>
      )}
    />
  );
}

const SYNC_COUNTS = [
  ['albums_created', 'new album', 'new albums'],
  ['albums_renamed', 'album renamed', 'albums renamed'],
  ['albums_moved', 'album moved to a corrected hill name', 'albums moved to corrected hill names'],
  ['photos_added', 'photo added', 'photos added'],
  ['photos_removed', 'photo taken out', 'photos taken out'],
];

function syncOutcome(last) {
  if (last.state === 'failed') return `It failed: ${last.error}`;
  const s = last.summary || {};
  const changes = SYNC_COUNTS.filter(([k]) => s[k]).map(([k, one, many]) => `${s[k].toLocaleString()} ${s[k] === 1 ? one : many}`);
  return `${changes.length ? changes.join(', ') : 'Nothing needed changing'}. ${(s.events || 0).toLocaleString()} event albums; ${(s.unsorted || 0).toLocaleString()} photos couldn't be placed (see "unsorted" in Immich).`;
}

// Photo albums: the photo sync (backend photo_sync.py) files the archive's
// photos into an album per event every night; "Sync now" runs it at once,
// e.g. after a folder of photos is added or renamed.
function PhotoSync({ session, onExpired }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    fetch('/api/site-admin/photo-sync', { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not check the photo sync');
        setStatus(data);
        setError('');
      })
      .catch(e => setError(e.message));
  }, [session, onExpired]);

  useEffect(() => { load(); }, [load]);

  // While a sync runs, look again every 10 seconds.
  const running = !!status?.running;
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [running, load]);

  const start = async () => {
    setStarting(true);
    setError('');
    const res = await fetch('/api/site-admin/photo-sync', {
      method: 'POST', headers: { Authorization: `Bearer ${session.session_token}` },
    }).catch(() => null);
    setStarting(false);
    if (res && res.status === 401) { onExpired(); return; }
    const data = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) setStatus(s => ({ ...s, running: true }));   // checked again in 10 s
    else if (res && res.status === 409) load();
    else setError(data.error || 'Could not start the sync. Please try again.');
  };

  const last = status?.last;
  const summary = !status ? '' : !status.enabled ? 'switched off here'
    : running ? 'syncing now…' : last?.finished_at ? `last synced ${when(last.finished_at)}` : 'every night at 3:30';

  return (
    <AdminSection id="photo-sync" title="Photo albums" summary={summary}>
      <p>
        Every night at 3:30 the photos in the photo archive are filed into an album for each event, and the
        photos page shows those albums. After adding photos, renaming a folder, fixing a photo&apos;s date in
        Immich or correcting a hill name in Results documents, press <strong>Sync now</strong> to update the
        albums straight away instead. It takes a few minutes.
      </p>
      {error && <p className="site-field-error" role="alert">{error}</p>}
      {status && !status.enabled && (
        <p className="site-admin-meta">The photo sync is switched off on this server (it only runs on the live website).</p>
      )}
      {status?.enabled && (
        <div className="site-admin-contact-foot">
          <span className="site-admin-meta" role="status">
            {running ? 'Syncing now… this page updates when it has finished.'
              : last?.finished_at ? `Last sync (${last.trigger}), ${when(last.finished_at)}: ${syncOutcome(last)}`
                : 'No sync since the website was last restarted; the next is tonight at 3:30.'}
          </span>
          <button type="button" className="site-btn site-btn--orange" onClick={start} disabled={running || starting}>
            {running || starting ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      )}
    </AdminSection>
  );
}

function noticeSummary(doc) {
  if (!doc.show) return 'off';
  if (!doc.until) return 'on';
  const until = new Date(`${doc.until}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return doc.until < todayInLondon() ? `ended ${until}` : `on until ${until}`;
}

function NoticeEditor(props) {
  return (
    <FieldPageEditor {...props} slug="notice" title="Notice bar" viewHref={links.home} summarize={noticeSummary}
      note="A short message in an orange bar at the top of every page, e.g. an event cancelled at short notice."
      renderFields={(doc, setDoc) => (
        <fieldset className="site-admin-group">
          <legend>Notice</legend>
          <label className="site-admin-check">
            <input type="checkbox" checked={!!doc.show} onChange={e => setDoc(setIn(doc, ['show'], e.target.checked))} />
            Show the notice
          </label>
          <label className="site-field">
            <span>Text <span className="site-admin-help">(one or two sentences, up to 200 characters)</span></span>
            <input type="text" value={doc.text || ''} maxLength={200} placeholder="Wiscombe on Sunday is cancelled: the paddock is waterlogged."
              onChange={e => setDoc(setIn(doc, ['text'], e.target.value))} />
          </label>
          <HomeLink doc={doc} path={['link']} label="Link (optional)" onChange={setDoc} />
          <label className="site-field site-admin-until">
            <span>Show until <span className="site-admin-help">(the end of that day; leave blank to show it until you switch it off)</span></span>
            <input type="date" value={doc.until || ''} onChange={e => setDoc(setIn(doc, ['until'], e.target.value))} />
          </label>
        </fieldset>
      )}
    />
  );
}

const isHillPage = slug => slug === 'hills' || slug.startsWith('hills-');

// One list of editable pages: the general pages, or (hills) the hills page and
// a page per venue.
function Pages({ session, onExpired, hills = false }) {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetch('/api/site-admin/pages', { headers: { Authorization: `Bearer ${session.session_token}` } })
      .then(async res => {
        if (res.status === 401) { onExpired(); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load');
        setPages(data.pages.filter(p => isHillPage(p.slug) === hills));
      })
      .catch(e => setError(e.message));
  }, [session, onExpired, hills]);

  useEffect(() => { load(); }, [load]);

  const edited = pages ? pages.filter(p => p.saved).length : 0;
  const summary = pages ? `${pages.length} pages${edited ? `, ${edited} edited` : ''}` : '';

  return (
    <AdminSection id={hills ? 'hills' : 'pages'} title={hills ? 'Hills' : 'Pages'} summary={summary}>
      {editing ? (
        <PageEditor slug={editing} session={session} onExpired={onExpired} onSaved={load} onClose={() => setEditing(null)} />
      ) : (
        <>
          {hills ? (
            <p>The hills page and a page for each venue. Every save is kept, so you can always go back to an earlier version. The grid of venues on the hills page and each hill&apos;s list of photo albums are added automatically; change a venue&apos;s picture in Hill pictures, below. Ask the website maintainer to add a new venue.</p>
          ) : (
            <p>The website pages you can edit. Every save is kept, so you can always go back to an earlier version. The archive and newsletter pages are fixed; ask the website maintainer to change those.</p>
          )}
          {error && <p className="site-field-error">{error}</p>}
          {!pages && !error && <p>Loading…</p>}
          {pages && (
            <ul className="site-admin-pages">
              {pages.map(p => (
                <li key={p.slug}>
                  <div>
                    <strong>{p.title}</strong> <a href={`/${p.slug}`} target="_blank" rel="noreferrer">/{p.slug}</a>
                    <span className="site-admin-meta">
                      {p.saved ? `Last changed by ${p.updated_by}, ${when(p.updated_at)}` : 'Starting text from the old website'}
                    </span>
                  </div>
                  <button type="button" className="site-btn site-btn--ink" onClick={() => setEditing(p.slug)}>Edit</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </AdminSection>
  );
}

export default function SiteAdmin() {
  const [session, setSession] = useState(readSession);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Web Admin · NHCA';
    return () => { document.title = previous; };
  }, []);

  // Magic link: swap the one-time token for a session, then tidy the URL.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) return;
    window.history.replaceState(null, '', '/site-admin');
    fetch(`/api/site-admin/verify?token=${encodeURIComponent(token)}`)
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'That link did not work.');
        writeSession(data);
        setSession(data);
      })
      .catch(e => setVerifyError(e.message));
  }, []);

  const expire = useCallback(() => { writeSession(null); setSession(null); }, []);

  const logout = async () => {
    await fetch('/api/site-admin/logout', { method: 'POST', headers: { Authorization: `Bearer ${session.session_token}` } }).catch(() => {});
    expire();
  };

  return (
    <SiteLayout>
      <section className="site-dark site-page-head">
        <div className="site-wrap site-section-head">
          <div>
            <span className="site-kicker">NHCA website</span>
            <h1 className="site-h2">Web Admin</h1>
          </div>
          {session && (
            <div className="site-admin-who">
              <span>{session.email}</span>
              <button type="button" className="site-btn site-btn--ghost" onClick={logout}>Log out</button>
            </div>
          )}
        </div>
      </section>
      <div className="site-wrap site-admin">
        {verifyError && <p className="site-field-error" role="alert">{verifyError}</p>}
        {session ? (
          <>
            <NoticeEditor session={session} onExpired={expire} />
            <HomeEditor session={session} onExpired={expire} />
            <PhotosPageEditor session={session} onExpired={expire} />
            <PhotoSync session={session} onExpired={expire} />
            <VideosPageEditor session={session} onExpired={expire} />
            <Pages session={session} onExpired={expire} />
            <Pages session={session} onExpired={expire} hills />
            <HillPicturesEditor session={session} onExpired={expire} />
            <Contacts session={session} onExpired={expire} />
            <ResultsDocs session={session} onExpired={expire} Section={AdminSection} />
            <EventVideos session={session} onExpired={expire} Section={AdminSection} />
          </>
        ) : <Login />}
      </div>
    </SiteLayout>
  );
}
